import { useState } from 'react';
import styled from 'styled-components';
import { Button, Box } from '@mui/material';
import { gql, useLazyQuery } from '@apollo/client';
import { StyledTextField } from '../../utils/CommonStyles';
import { IProject } from '../../types/project';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import { unlockPortal } from '../../utils/portalAccess';

interface Props {
  project: IProject;
  onUnlock: () => void;
}

interface IVerifyPortalPassword {
  confirmPassword: boolean;
}

const PortalPasswordScreen = ({ project, onUnlock }: Props) => {
  const storeDispatch = useAppDispatch();
  const [password, setPassword] = useState('');

  const [verifyPortalPassword, { loading }] = useLazyQuery<IVerifyPortalPassword>(VERIFY_PORTAL_PASSWORD, {
    onCompleted: (data) => {
      if (data.confirmPassword) {
        unlockPortal(project._id);
        onUnlock();
      } else {
        storeDispatch(showErrorSnackbar('Incorrect password'));
      }
    },
    onError: () => {
      storeDispatch(showErrorSnackbar('Something went wrong verifying the password. Please try again.'));
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    verifyPortalPassword({ variables: { project: project._id, password } });
  };

  return (
    <Container>
      <LogoImgContainer>
        <LogoImg src="https://s3.ca-central-1.amazonaws.com/app.rdsre.ca/logo.png" />
      </LogoImgContainer>
      <PasswordPrompt>
        <strong>This page is password protected. Please enter the password to continue.</strong>
        <PasswordForm id="portalPasswordForm" onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            label="Password"
            type="password"
            autoComplete="off"
            variant="outlined"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </PasswordForm>
        <Box sx={{ marginTop: '10px' }}>
          <Button type="submit" variant="contained" form="portalPasswordForm" size="large" disabled={loading}>
            Enter
          </Button>
        </Box>
      </PasswordPrompt>
    </Container>
  );
};

export default PortalPasswordScreen;

const VERIFY_PORTAL_PASSWORD = gql`
  query confirmPassword($project: MongoID!, $password: String!) {
    confirmPassword(project: $project, password: $password)
  }
`;

const Container = styled.div`
  left: 0;
  right: 0;
  background: white;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding-bottom: 5%;

  @media (max-width: 850px) {
    width: 100% !important;
  }

  @media (max-height: 700px) {
    padding-top: 0;
  }
`;

const LogoImgContainer = styled.div`
  height: 100px;
  width: 70%;
  margin: 10% 0 20px 0;

  display: flex;
`;

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const PasswordPrompt = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 70%;
  max-height: 220px;
`;

const PasswordForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 20px;
`;
